import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Shield, Lock, Key, Eye, EyeOff, AlertTriangle, CheckCircle2, XCircle, Clock, TrendingUp, TrendingDown, Users, Ticket, Calendar, Download, Filter, Search,
  RefreshCw, Settings, Plus, Edit, Trash2, Save, List, Bell, Mail, Phone, MessageSquare, Heart, Award, Target, Cpu, Database, Monitor, UserPlus, UserMinus, Send, Archive, Unarchive, History, Clock3, Star, Crown,
  Timer, Brain, Route, Lightning, HardDrive, MapPin, Globe, Smartphone, File, FileVideo, FileAudio, FileImage,
  FileSpreadsheet, FilePdf, FileZip, FileCheck, FileX, FilePlus, FileMinus, FileEdit, FileSearch, FileSlash, FileWarning,
  FileQuestion, FileHeart, FileClock, FileUp, FileDown, FileLeft, FileRight, FileBarChart, FilePieChart, FileLineChart,
  FileScissors, FileType, FileSymlink, FileStack, FileTree, FileBinary, FileCog, FileSettings, FileLock, FileUnlock,
  FileKey, FileSignature, FileVerified, FileBadge, FileTag, FileLabel, FileBookmark, FileStar, FileFlag,
  FileAlert, FileInfo, FileHelp, FileMessage, FileComment, FileChat, FileMail, FilePhone, Activity, BarChart3, PieChart, LineChart
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const SecurityPolicies = () => {
  const [securityData, setSecurityData] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [accessControlRules, setAccessControlRules] = useState([]);
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h'); // Default to 24 hours
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showResolvedAlerts, setShowResolvedAlerts] = useState(false);

  const loadSecurityData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const securityResponse = await new AdminService().getSecurityMetrics(timeRange);
      const policiesResponse = await new AdminService().getSecurityPolicies();
      const accessControlResponse = await new AdminService().getAccessControlRules();
      const alertsResponse = await new AdminService().getSecurityAlerts();

      if (securityResponse.success && policiesResponse.success && accessControlResponse.success && alertsResponse.success) {
        setSecurityData(securityResponse.data);
        setPolicies(policiesResponse.data);
        setAccessControlRules(accessControlResponse.data);
        setSecurityAlerts(alertsResponse.data);
      } else {
        setError(securityResponse.message || policiesResponse.message || accessControlResponse.message || alertsResponse.message);
      }
    } catch (err) {
      setError('Failed to fetch security data.');
      console.error('Error fetching security data:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadSecurityData();
    const interval = setInterval(loadSecurityData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [loadSecurityData]);

  const handleCreatePolicy = async (policyData) => {
    if (window.confirm('Are you sure you want to create this security policy?')) {
      try {
        const response = await new AdminService().createSecurityPolicy(policyData);
        if (response.success) {
          alert('Security policy created successfully!');
          loadSecurityData(); // Refresh data
        } else {
          alert(`Failed to create policy: ${response.message}`);
        }
      } catch (err) {
        alert('Error creating policy.');
        console.error('Error creating policy:', err);
      }
    }
  };

  const handleUpdatePolicy = async (policyId, policyData) => {
    if (window.confirm('Are you sure you want to update this security policy?')) {
      try {
        const response = await new AdminService().updateSecurityPolicy(policyId, policyData);
        if (response.success) {
          alert('Security policy updated successfully!');
          loadSecurityData(); // Refresh data
        } else {
          alert(`Failed to update policy: ${response.message}`);
        }
      } catch (err) {
        alert('Error updating policy.');
        console.error('Error updating policy:', err);
      }
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (window.confirm('Are you sure you want to delete this security policy?')) {
      try {
        const response = await new AdminService().deleteSecurityPolicy(policyId);
        if (response.success) {
          alert('Security policy deleted successfully!');
          loadSecurityData(); // Refresh data
        } else {
          alert(`Failed to delete policy: ${response.message}`);
        }
      } catch (err) {
        alert('Error deleting policy.');
        console.error('Error deleting policy:', err);
      }
    }
  };

  const handleResolveAlert = async (alertId) => {
    if (window.confirm('Are you sure you want to resolve this security alert?')) {
      try {
        const response = await new AdminService().resolveSecurityAlert(alertId);
        if (response.success) {
          alert('Security alert resolved successfully!');
          setSecurityAlerts(prev => prev.filter(alert => alert.id !== alertId));
        } else {
          alert(`Failed to resolve alert: ${response.message}`);
        }
      } catch (err) {
        alert('Error resolving alert.');
        console.error('Error resolving alert:', err);
      }
    }
  };

  const getPolicyStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'text-blue-600 bg-blue-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getAccessLevelColor = (level) => {
    switch (level) {
      case 'admin': return 'text-red-600 bg-red-100';
      case 'manager': return 'text-orange-600 bg-orange-100';
      case 'agent': return 'text-blue-600 bg-blue-100';
      case 'user': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading security data...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">Error: {error}</div>;
  }

  if (!securityData) {
    return <div className="p-6 text-center text-gray-500">No security data available.</div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <Shield className="mr-3 text-blue-600" size={30} />
          Security Policies Management
        </h1>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1h">Last 1 Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Categories</option>
            <option value="authentication">Authentication</option>
            <option value="authorization">Authorization</option>
            <option value="data_protection">Data Protection</option>
            <option value="network">Network Security</option>
            <option value="system">System Security</option>
          </select>
          <button
            onClick={loadSecurityData}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <RefreshCw className="mr-2" size={18} /> Refresh
          </button>
        </div>
      </div>

      <p className="text-gray-600 mb-8">
        Comprehensive security policy management and monitoring for system protection.
        <span className="ml-2 text-sm text-gray-500">Last Updated: {new Date(securityData.lastUpdated).toLocaleString()}</span>
      </p>

      {/* Security Overview */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <BarChart3 className="mr-2 text-purple-600" size={24} /> Security Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {securityData.overviewMetrics.map((metric, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-lg font-medium text-gray-600">{metric.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{metric.value}</p>
                <div className="flex items-center mt-2 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPolicyStatusColor(metric.status)}`}>
                    {metric.status}
                  </span>
                  <span className="ml-3 text-gray-500">{metric.description}</span>
                </div>
              </div>
              {metric.type === 'active_policies' && <Shield size={40} className="text-blue-500 opacity-70" />}
              {metric.type === 'security_violations' && <AlertTriangle size={40} className="text-red-500 opacity-70" />}
              {metric.type === 'access_attempts' && <Key size={40} className="text-green-500 opacity-70" />}
              {metric.type === 'threat_level' && <Target size={40} className="text-yellow-500 opacity-70" />}
            </div>
          ))}
        </div>
      </section>

      {/* Security Policies */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Lock className="mr-2 text-green-600" size={24} /> Security Policies
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <Plus className="mr-2" size={18} /> Create Policy
                </button>
              </div>
              <div className="text-sm text-gray-500">
                {policies.length} policies configured
              </div>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Policy Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Modified
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {policies.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No security policies configured.</td>
                </tr>
              ) : (
                policies.map((policy) => (
                  <tr key={policy.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{policy.name}</div>
                      <div className="text-sm text-gray-500">{policy.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {policy.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPolicyStatusColor(policy.status)}`}>
                        {policy.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSeverityColor(policy.priority)}`}>
                        {policy.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(policy.lastModified).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleUpdatePolicy(policy.id, policy)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Edit Policy"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeletePolicy(policy.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Policy"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Access Control Rules */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Key className="mr-2 text-blue-600" size={24} /> Access Control Rules
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rule Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Access Level
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Resource
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissions
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {accessControlRules.map((rule, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                    <div className="text-sm text-gray-500">{rule.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getAccessLevelColor(rule.accessLevel)}`}>
                      {rule.accessLevel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {rule.resource}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {rule.permissions.join(', ')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPolicyStatusColor(rule.status)}`}>
                      {rule.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-blue-600 hover:text-blue-900 mr-3" title="Edit Rule">
                      <Edit size={18} />
                    </button>
                    <button className="text-red-600 hover:text-red-900" title="Delete Rule">
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Security Alerts */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Bell className="mr-2 text-red-600" size={24} /> Security Alerts
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showResolvedAlerts}
                    onChange={(e) => setShowResolvedAlerts(e.target.checked)}
                    className="mr-2"
                  />
                  Show Resolved Alerts
                </label>
              </div>
              <div className="text-sm text-gray-500">
                {securityAlerts.filter(alert => showResolvedAlerts || alert.status !== 'resolved').length} active alerts
              </div>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alert
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Detected
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {securityAlerts.filter(alert => showResolvedAlerts || alert.status !== 'resolved').length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No security alerts found.</td>
                </tr>
              ) : (
                securityAlerts.filter(alert => showResolvedAlerts || alert.status !== 'resolved').map((alert) => (
                  <tr key={alert.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{alert.title}</div>
                      <div className="text-sm text-gray-500">{alert.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {alert.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSeverityColor(alert.severity)}`}>
                        {alert.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(alert.detectedAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {alert.status !== 'resolved' && (
                        <button
                          onClick={() => handleResolveAlert(alert.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Resolve Alert"
                        >
                          <CheckCircle2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Security Monitoring */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Monitor className="mr-2 text-purple-600" size={24} /> Security Monitoring
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {securityData.monitoringMetrics.map((metric, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <h3 className="text-xl font-medium text-gray-800 mb-2">{metric.name}</h3>
              <p className="text-gray-600 text-sm mb-3">{metric.description}</p>
              <div className="flex items-center justify-between mb-4">
                <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
                <div className="flex items-center text-sm">
                  {metric.trend > 0 ? (
                    <TrendingUp className="text-green-500 mr-1" size={16} />
                  ) : (
                    <TrendingDown className="text-red-500 mr-1" size={16} />
                  )}
                  <span className={metric.trend > 0 ? 'text-green-600' : 'text-red-600'}>
                    {Math.abs(metric.trend)}%
                  </span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${metric.value >= 80 ? 'bg-green-500' : metric.value >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${metric.value}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Security Trends */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <TrendingUp className="mr-2 text-green-600" size={24} /> Security Trends
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {securityData.trendMetrics.map((trend, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-1">{trend.value}</div>
                <div className="text-sm text-gray-600 mb-2">{trend.name}</div>
                <div className="flex items-center justify-center text-sm">
                  {trend.change > 0 ? (
                    <TrendingUp className="text-green-500 mr-1" size={16} />
                  ) : (
                    <TrendingDown className="text-red-500 mr-1" size={16} />
                  )}
                  <span className={trend.change > 0 ? 'text-green-600' : 'text-red-600'}>
                    {Math.abs(trend.change)}%
                  </span>
                  <span className="ml-1 text-gray-500">vs last period</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SecurityPolicies;
